import { FC, Fragment, useEffect, useState } from 'react'
import { Modal as AModal } from 'antd'
import jclasses from '@pages/Admin/CRM/styles/jsmodal.module.css'
import Modal from '@common/Modal'
import ActionModal from '../../../ActionModal'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { useAddAlert } from '@/hooks/useAddAlert'
import { getApiErrorMessage } from '@/helpers/getApiErrorMessage'
import CourseRow from './components/CourseRow/CourseRow'
import BlockRow from './components/BlockRow/BlockRow'
import ThemeRow from './components/ThemeRow/ThemeRow'
import LessonRow from './components/LessonRow/LessonRow'
import {
  getStudentLearningDynamics,
  resetBlockProgress,
  resetCourseProgress,
  resetLessonProgress,
  setStudentAccessLevel,
  updateLessonProgress,
} from './utils/api'
import {
  getAccessActionKey,
  getAccessConfirmationText,
  getTargetAccessLevel,
} from './utils/access'
import {
  AccessChangeAction,
  DynamicsModalProps,
  LearningDynamicsBlock,
  LearningDynamicsCourse,
  LearningDynamicsLesson,
  StudentLearningDynamicsProps,
} from './utils/types'
import styles from './index.module.css'

const DynamicsModal = Modal as unknown as FC<DynamicsModalProps>

interface ApiError {
  response?: {
    status?: number
  }
}

const StudentLearningDynamics = ({
  student,
}: StudentLearningDynamicsProps) => {
  const addAlert = useAddAlert()
  const studentId = student?.id ?? null
  const [learningDynamics, setLearningDynamics] = useState<
    LearningDynamicsCourse[]
  >([])
  const [courseToReset, setCourseToReset] =
    useState<LearningDynamicsCourse | null>(null)
  const [lessonToReset, setLessonToReset] = useState<{
    lesson: LearningDynamicsLesson
    courseId: number
  } | null>(null)
  const [pendingActionKey, setPendingActionKey] = useState<string | null>(null)

  const fetchDynamics = async () => {
    if (!studentId) {
      setLearningDynamics([])
      return false
    }

    try {
      const courses = await getStudentLearningDynamics(studentId)
      setLearningDynamics(courses)
      return true
    } catch (error) {
      addAlert({
        title: 'Возникла ошибка при загрузке данных!',
        text: getApiErrorMessage(error, 'Не удалось загрузить динамику обучения'),
        type: 'error',
      })
      return false
    }
  }

  useEffect(() => {
    fetchDynamics()
  }, [studentId])

  const getAccessErrorText = (
    error: unknown,
    action: AccessChangeAction,
  ) => {
    const status = (error as ApiError)?.response?.status

    if (status === 404) {
      return 'Активный заказ по этому курсу не найден'
    }

    if (
      status === 422 &&
      getTargetAccessLevel(action) === 'content_only' &&
      (action.workType === 'control' || action.workType === 'probe')
    ) {
      return 'У контрольной и пробника нет режима только контента'
    }

    return getApiErrorMessage(error, 'Не удалось изменить доступ')
  }

  const updateAccess = async (action: AccessChangeAction) => {
    if (!studentId) return

    const actionKey = getAccessActionKey(
      action.entityType,
      action.entityId,
      action.column,
    )
    const accessLevel = getTargetAccessLevel(action)
    setPendingActionKey(actionKey)

    try {
      await setStudentAccessLevel({
        child_id: studentId,
        entity_type: action.entityType,
        entity_id: action.entityId,
        access_level: accessLevel,
      })
    } catch (error) {
      addAlert({
        title: 'Возникла ошибка',
        text: getAccessErrorText(error, action),
        type: 'error',
      })
      setPendingActionKey(null)
      return
    }

    try {
      const courses = await getStudentLearningDynamics(studentId)
      setLearningDynamics(courses)
      addAlert({
        title: accessLevel === 'closed' ? 'Доступ закрыт' : 'Доступ открыт',
      })
    } catch (error) {
      addAlert({
        title: 'Доступ изменён, но данные не обновились',
        text: getApiErrorMessage(error, 'Обновите динамику обучения повторно'),
        type: 'error',
      })
    } finally {
      setPendingActionKey(null)
    }
  }

  const confirmAccessChange = (action: AccessChangeAction) => {
    AModal.confirm({
      title: 'Подтвердите',
      content: getAccessConfirmationText(action),
      icon: null,
      centered: true,
      maskClosable: true,
      className: jclasses.modal,
      okText: 'Подтвердить',
      cancelText: 'Отменить',
      onOk: () => updateAccess(action),
    })
  }

  const onCourseReset = async () => {
    if (!studentId || !courseToReset) return

    try {
      const isSuccess = await resetCourseProgress(
        studentId,
        courseToReset.direction_id,
      )

      if (!isSuccess) throw new Error('Не удалось сбросить статистику по курсу')

      await fetchDynamics()
      addAlert({ title: 'Статистика по курсу сброшена!' })
    } catch (error) {
      addAlert({
        title: 'Возникла ошибка',
        text: getApiErrorMessage(error, 'Не удалось сбросить статистику'),
        type: 'error',
      })
    } finally {
      setCourseToReset(null)
    }
  }

  const onLessonReset = async () => {
    if (!studentId || !lessonToReset) return

    try {
      const isSuccess = await resetLessonProgress(
        studentId,
        lessonToReset.courseId,
        lessonToReset.lesson.id,
      )

      if (!isSuccess) throw new Error('Не удалось сбросить статистику по уроку')

      await fetchDynamics()
      addAlert({ title: 'Статистика сброшена!' })
    } catch (error) {
      addAlert({
        title: 'Возникла ошибка',
        text: getApiErrorMessage(error, 'Не удалось сбросить статистику'),
        type: 'error',
      })
    } finally {
      setLessonToReset(null)
    }
  }

  const onBlockReset = (block: LearningDynamicsBlock) => {
    if (!studentId) return

    AModal.confirm({
      title: 'Подтвердите',
      content: `Вы уверены что хотите сбросить прогресс ${block.name || ''}`,
      icon: null,
      centered: true,
      maskClosable: true,
      className: jclasses.modal,
      okText: 'Подтвердить',
      cancelText: 'Отменить',
      onOk: async () => {
        try {
          const isSuccess = await resetBlockProgress(studentId, block.id)
          if (!isSuccess) throw new Error('Не удалось сбросить прогресс блока')

          await fetchDynamics()
          addAlert({ title: 'Прогресс сброшен' })
        } catch (error) {
          addAlert({
            title: 'Возникла ошибка',
            text: getApiErrorMessage(error, 'Не удалось сбросить прогресс'),
            type: 'error',
          })
        }
      },
    })
  }

  const onUpdateProgress = async (
    lesson: LearningDynamicsLesson,
    progress: number,
  ) => {
    if (!studentId) return false

    try {
      const isSuccess = await updateLessonProgress(
        studentId,
        lesson.id,
        progress,
      )
      if (!isSuccess) throw new Error('Не удалось обновить прогресс')

      addAlert({ title: 'Прогресс обновлен' })
      await fetchDynamics()
      return true
    } catch (error) {
      addAlert({
        title: 'Возникла ошибка обновления прогресса',
        text: getApiErrorMessage(error, 'Не удалось обновить прогресс'),
        type: 'error',
      })
      return false
    }
  }

  const accessDisabled = pendingActionKey !== null

  return (
    <>
      <div className={styles.container}>
        <div className={styles.row}>
          <div className={styles.headerCell}>Название курса</div>
          <div className={styles.headerCell}>Дата начала</div>
          <div className={styles.headerCell}>Дата окончания</div>
          <div className={styles.headerCell}>Успех</div>
          <TooltipWrapper
            title="Материалы урока видны ученику"
            placement="top"
          >
            <div className={styles.headerCell}>Материалы</div>
          </TooltipWrapper>
          <TooltipWrapper
            title="Ученик может выполнять задания; материалы при этом всегда открыты"
            placement="top"
          >
            <div className={styles.headerCell}>Задания</div>
          </TooltipWrapper>
          <TooltipWrapper title="Сбросить прогресс" placement="top">
            <div className={styles.headerCell}>Сброс</div>
          </TooltipWrapper>
        </div>

        {learningDynamics.map((course) => (
          <CourseRow
            key={course.direction_id}
            course={course}
            onReset={() => setCourseToReset(course)}
          >
            {course.blocks?.map((block) => (
              <BlockRow
                key={block.id}
                block={block}
                accessDisabled={accessDisabled}
                pendingActionKey={pendingActionKey}
                onAccessChange={confirmAccessChange}
                onReset={() => onBlockReset(block)}
              >
                {block.themes?.map((theme) => {
                  // пробник и контрольная — тема из одной работы,
                  // раскрывать нечего: показываем работу строкой
                  const isSingleWork = theme.work_type !== 'homework'

                  const lessons = theme.lessons?.map((lesson) => (
                    <LessonRow
                      key={lesson.id}
                      lesson={lesson}
                      themeName={theme.name}
                      standalone={isSingleWork}
                      accessDisabled={accessDisabled}
                      pendingActionKey={pendingActionKey}
                      onAccessChange={confirmAccessChange}
                      onUpdateProgress={onUpdateProgress}
                      onReset={() =>
                        setLessonToReset({
                          lesson,
                          courseId: course.direction_id,
                        })
                      }
                    />
                  ))

                  if (isSingleWork) {
                    return <Fragment key={theme.id}>{lessons}</Fragment>
                  }

                  return (
                    <ThemeRow
                      key={theme.id}
                      theme={theme}
                      accessDisabled={accessDisabled}
                      pendingActionKey={pendingActionKey}
                      onAccessChange={confirmAccessChange}
                    >
                      <div className={styles.row}>
                        <div className={styles.subHeaderCell}>
                          Название урока
                        </div>
                        <div className={styles.subHeaderCell}>Дата начала</div>
                        <div className={styles.subHeaderCell}>
                          Дата окончания
                        </div>
                        <div className={styles.subHeaderCell}>Успех</div>
                        <div className={styles.subHeaderCell}>Материалы</div>
                        <div className={styles.subHeaderCell}>Задания</div>
                        <div className={styles.subHeaderCell}>Сброс</div>
                      </div>
                      {lessons}
                    </ThemeRow>
                  )
                })}
              </BlockRow>
            ))}
          </CourseRow>
        ))}
      </div>

      <DynamicsModal
        active={!!lessonToReset}
        setActive={() => setLessonToReset(null)}
        className={styles.modalWrapper}
      >
        <ActionModal
          title="Сбросить статистику"
          content={`Вы уверены что хотите сбросить статистику по ${lessonToReset?.lesson.lesson_name ?? ''}?`}
          onDismiss={() => setLessonToReset(null)}
          onAction={onLessonReset}
        />
      </DynamicsModal>

      <DynamicsModal
        active={!!courseToReset}
        setActive={() => setCourseToReset(null)}
        className={styles.modalWrapper}
      >
        <ActionModal
          title="Сбросить статистику"
          content={`Вы уверены что хотите сбросить статистику по ${courseToReset?.course_name ?? ''}?`}
          onDismiss={() => setCourseToReset(null)}
          onAction={onCourseReset}
        />
      </DynamicsModal>
    </>
  )
}

export default StudentLearningDynamics
